import React, { ReactElement, useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../store/hooks';
import { isDev } from '../shared/utils';

import { cleanupAsync, isDesynced, updateWindowsAsync } from '../store/windowsSlice';
import { useLiveQuery } from 'dexie-react-hooks';
import { getBackupEveryNSeconds, getIsBackupOverdue, getLatestBackup, getBackupStatus, BackupStatus, getTabvanaWindowId } from '../db/prefs';
import { requestBackupPermission, setBackupDir, maybeBackup } from '../db/backup';
import { faArrowsRotate, faBroom, faFloppyDisk, faGear, faPlugCircleBolt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { openOptionsPageInWindow } from '../chrome/chromeActions';
import IconButton from '@mui/joy/IconButton';
import { Box, Button, CircularProgress, Tooltip } from '@mui/joy';

import { useActiveProgress, clearAllProgress, useCompletedProgress } from '../db/progress';
import { clearQueue } from '../db/queue';

function formatTimeAgo(ts: number | undefined): string {
  if (!ts) return '';
  const diffMin = Math.floor((Date.now() - ts) / 60_000);
  if (diffMin < 1) return 'Backed up just now';
  if (diffMin < 60) return `Backed up ${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `Backed up ${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `Backed up ${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
}

const GlobalButtons = React.memo((): ReactElement => {
  const tabvanaWindowId = useLiveQuery(getTabvanaWindowId);
  const isBackupOverdue = useLiveQuery(getIsBackupOverdue);
  const latestBackup = useLiveQuery(getLatestBackup);
  const backupStatus = useLiveQuery(getBackupStatus);
  const { totalWork, completedWork, activeTasks, operations } = useActiveProgress();
  const completedProgressTasks = useCompletedProgress();

  // Tick every 30s so "X minutes ago" stays fresh
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  const dispatch = useAppDispatch();
  const desynced = useAppSelector(isDesynced);
  const working = activeTasks > 0;
  const isBackingUp = backupStatus === BackupStatus.BackupInProgress;

  const handleCancelTasks = async () => {
    await clearQueue();
    await clearAllProgress();
  };

  const tooltipContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '200px' }}>
      {operations.length > 0 ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span><strong>Total: {completedWork} / {totalWork}</strong></span>
          </div>
          {Object.entries(
            operations.reduce((acc, op) => {
              if (!acc[op.type]) acc[op.type] = { current: 0, total: 0 };
              acc[op.type].current += op.current;
              acc[op.type].total += op.total;
              return acc;
            }, {} as Record<string, { current: number; total: number }>)
          ).map(([type, stats]) => {
            const label = type === 'generateEmbedding' 
              ? 'Generating embeddings' 
              : type === 'backup' 
                ? 'Backup in progress' 
                : type === 'renormalize'
                  ? 'Renormalizing database'
                  : type === 'similarity'
                    ? 'Calculating similarities'
                    : type.charAt(0).toUpperCase() + type.slice(1);
            
            return (
              <div key={type}>
                {label}: {stats.current} / {stats.total}
              </div>
            );
          })}
          <Button size="sm" color="danger" variant="soft" onClick={handleCancelTasks} sx={{ mt: 1 }} fullWidth>
            Cancel Tasks
          </Button>
        </>
      ) : (
        <div>No active background tasks</div>
      )}
      {completedProgressTasks.length > 0 && (
        <>
          <div style={{ borderTop: '1px solid var(--joy-palette-divider)', marginTop: '8px', paddingTop: '8px', fontSize: '0.85em', color: 'var(--joy-palette-text-tertiary)' }}>
            <strong>Completed Tasks ({completedProgressTasks.length})</strong>
          </div>
          {completedProgressTasks.map((op, i) => {
            const label = op.type === 'generateEmbedding' 
              ? 'Generated embeddings' 
              : op.type === 'backup' 
                ? 'Backup completed' 
                : op.type === 'renormalize'
                  ? 'Renormalized database'
                  : op.type === 'similarity'
                    ? 'Calculated similarities'
                    : op.type.charAt(0).toUpperCase() + op.type.slice(1) + ' completed';
            return (
              <div key={op.id || i} style={{ fontSize: '0.85em', color: 'var(--joy-palette-text-tertiary)' }}>
                {label} ({op.total})
              </div>
            );
          })}
        </>
      )}
    </div>
  );

  return (
    <div
      style={{
        position: 'absolute',
        top: '2px',
        right: '10px',
        zIndex: '999',
      }}
    >
      {latestBackup != null && latestBackup > 0 && (
        <span style={{ fontSize: '0.7rem', color: 'var(--joy-palette-text-tertiary)', verticalAlign: 'middle', marginRight: '4px' }}>
          {isBackingUp && (
            <Box
              component="span"
              sx={{
                marginRight: '4px',
                display: 'inline-block',
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%': { opacity: 0.5 },
                  '50%': { opacity: 1 },
                  '100%': { opacity: 0.5 },
                },
              }}
            >
              <FontAwesomeIcon icon={faFloppyDisk} />
            </Box>
          )}
          {formatTimeAgo(latestBackup)}
        </span>
      )}
      <Tooltip title={tooltipContent} placement="bottom" sx={{ zIndex: 99999 }}>
        <CircularProgress
          size="sm"
          determinate={activeTasks === 0 || totalWork > 0}
          value={activeTasks === 0 ? 0 : (totalWork > 0 ? (completedWork / totalWork) * 100 : undefined)}
          color={activeTasks > 0 ? 'primary' : 'neutral'}
          sx={{
            verticalAlign: 'middle',
            margin: '0.2rem',
            ...(activeTasks > 0 && {
              animation: 'pulse 1.5s ease-in-out infinite',
              '@keyframes pulse': {
                '0%': { opacity: 0.6 },
                '50%': { opacity: 1 },
                '100%': { opacity: 0.6 },
              },
            }),
          }}
        >
          {/* totalWork > 0 && <span style={{fontSize: '0.6rem'}}>{Math.round((completedWork / totalWork) * 100)}%</span> */}
        </CircularProgress>
      </Tooltip>
      {isBackupOverdue && (
        <Button
          color="danger"
          onClick={async () => {
            if (await requestBackupPermission()) {
              await maybeBackup(true);
              return;
            }
            await setBackupDir();
            await maybeBackup(true);
          }}
        >
          Set Backup Dir
        </Button>
      )}
      {desynced && (
        <Tooltip title="Force Update" placement="bottom" sx={{ zIndex: 99999 }}>
          <Button
            size="sm"
            variant="soft"
            onClick={() => dispatch(updateWindowsAsync())}
            title="Force Update"
            color="danger"
          >
            <FontAwesomeIcon icon={faArrowsRotate} />
          </Button>
        </Tooltip>
      )}
      {isDev && (
        <>
          <span style={{ fontSize: '0.65rem', color: 'var(--joy-palette-text-tertiary)', verticalAlign: 'middle', marginRight: '4px' }}>
            {__COMMIT_HASH__} · {new Date(__BUILD_DATE__).toLocaleString()}
          </span>
          <Tooltip title="Reload Extension (Dev)" placement="bottom" sx={{ zIndex: 99999 }}>
            <IconButton onClick={() => chrome.runtime.reload()} title="Reload Extension">
              <FontAwesomeIcon icon={faPlugCircleBolt} />
            </IconButton>
          </Tooltip>
        </>
      )}
      <Tooltip title="Clean Up: Stash untagged, Snooze all." placement="bottom" sx={{ zIndex: 99999 }}>
        <IconButton>
          <FontAwesomeIcon
            icon={faBroom}
            onClick={() => {
              dispatch(cleanupAsync(tabvanaWindowId));
            }}
            title="Clean Up"
          />
        </IconButton>
      </Tooltip>

      <Tooltip title="Settings" placement="bottom" sx={{ zIndex: 99999 }}>
        <IconButton>
          <FontAwesomeIcon
            icon={faGear}
            onClick={() => openOptionsPageInWindow()}
            title="Settings"
          />
        </IconButton>
      </Tooltip>
    </div>
  );
});

export default GlobalButtons;
